export interface GroupableSlackMessage {
	id: string
	slackId: string
	slackThreadId?: string | null
	createdAt: number
}

/**
 * The top-level Slack messages that joined this thread after its first one, by the
 * channel's grouping window. The message the team already answers in stays, so
 * the same rule as the server's split decides what can move.
 */
export function joinedSlackMessages<T extends GroupableSlackMessage>(
	messages: readonly T[],
	claimedTs: string | null | undefined,
): T[] {
	const topLevel = messages
		.filter((m) => !m.slackId.startsWith("temp_"))
		.filter((m) => !m.slackThreadId || m.slackThreadId === m.slackId)
		.sort((a, b) => a.createdAt - b.createdAt)
	return topLevel.slice(1).filter((message) => message.slackId !== claimedTs)
}

/** The window a channel groups by: its own override, else its Slack workspace's, else the default. */
export function groupingWindowMinutes({
	channelMinutes,
	workspaceMinutes,
}: {
	channelMinutes?: number | null
	workspaceMinutes?: number | null
}): number {
	return channelMinutes ?? workspaceMinutes ?? 30
}

export function formatGroupingWindow(minutes: number): string {
	if (minutes % 1440 === 0) {
		const days = minutes / 1440
		return days === 1 ? "24 hours" : `${days} days`
	}
	if (minutes % 60 === 0) {
		const hours = minutes / 60
		return `${hours} hour${hours === 1 ? "" : "s"}`
	}
	return `${minutes} minutes`
}
